/**
 * React Query hooks for the Cockpit Ops tool.
 *
 *   useCockpitOpsJobs()    GET {API_BASE}/v1/cockpit-ops/jobs
 *   useCockpitOpsHealth()  GET {API_BASE}/v1/cockpit-ops/health
 *
 * Backend is the api service (api.ampersandbrands.com). `/jobs` returns the
 * monitored-job registry from `cockpit_ops_jobs` (`{ data: [...] }`); `/health`
 * returns the inverted-heartbeat watcher's latest verdict per job_id
 * (`{ rows: [...] }`). The page joins jobs to verdicts by job_id to render a
 * colored health pill per row (green=ok, amber=late, red=failed, gray=unknown).
 *
 * RBAC: server-side enforced. Any signed-in employee in the viewer or admin
 * Entra group can read.
 */

import { useQuery } from '@tanstack/react-query';
import { timeAgo } from '@ampersandbrands/ui';
import { apiGet } from './api';
import { API_BASE } from './apiBase';
import { formatDateTime as _formatDateTime } from './dateUtils';

export const COCKPIT_OPS_API_BASE = `${API_BASE}/v1/cockpit-ops`;

export type HealthVerdict = 'ok' | 'late' | 'failed' | 'unknown';

export type TriggerType = 'schedule' | 'event' | 'manual';

/** A job's trigger, tolerating a missing or unknown field as 'schedule'. */
export function triggerTypeOf(job: Pick<RegistryJob, 'trigger_type'>): TriggerType {
  return job.trigger_type === 'event' || job.trigger_type === 'manual'
    ? job.trigger_type
    : 'schedule';
}

/** The verdict to show for a job. Event and manual jobs have no schedule to be
 *  late against, so a 'late' verdict from the API (older API, or a stale
 *  cache) is replaced by what the latest run says: ok after a success,
 *  unknown otherwise. Failed always stands. Mirrors the api rule. */
export function effectiveVerdict(job: RegistryJob, verdict: HealthVerdict): HealthVerdict {
  if (triggerTypeOf(job) === 'schedule' || verdict !== 'late') return verdict;
  return job.last_run_outcome === 'success' ? 'ok' : 'unknown';
}

export type HealthRow = {
  job_id: string;
  source: 'otto-it' | 'lambda';
  verdict: HealthVerdict;
  verdict_since: string;
  last_evaluated_at: string;
  detail: string | null;
};

export type CockpitOpsHealthResponse = {
  rows: HealthRow[];
  fetched_at: string;
};

export type UseCockpitOpsHealthResult = {
  health: Map<string, HealthRow>;
  rows: HealthRow[];
  fetchedAt: string | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
};

/** A monitored job from the api `cockpit_ops_jobs` registry (`/jobs` -> data[]).
 *  Enriched per the grid UX pass (api migration 0058 + read-endpoint join):
 *  a one-line short_description, a detail_steps list (the script's logic), the
 *  last run (last_seen_at), a computed next_run, the latest health verdict, and
 *  the last run's duration_ms (null until the heartbeat poster includes it). */
export type RegistryJob = {
  job_id: string;
  /** 'otto-it' | 'lambda' for old-registry rows; a job known only to the new
   *  job-monitoring list carries its open-text runs_on (a Lambda function
   *  name, 'steven-laptop', ...). Render a cloud icon for 'lambda', a server
   *  icon for anything else. */
  source: string;
  cadence_minutes: number;
  // api returns 0/1; treat as truthy.
  enabled: number | boolean;
  short_description: string | null;
  detail_steps: string[];
  last_seen_at: string | null;
  next_run: string | null;
  verdict: HealthVerdict;
  duration_ms: number | null;
  // Job-monitoring rebuild feed (spec job-monitoring-rebuild.md, 2026-08-25):
  // the new ops_job / ops_job_run tables FEED this page. All optional so a
  // not-yet-deployed api (keys absent) renders exactly as before. 'new' means
  // the job reports through the new start/finish path and the last_run_*
  // fields carry its latest run; 'old' means only the legacy registry knows it
  // (the page marks those rows so the migration gap stays visible).
  monitor?: 'new' | 'old';
  runs_on?: string | null;
  /** Expected minutes between runs, edited from Cockpit (not this page yet).
   *  interval_unset=true is a FLAGGED state: nobody has set the interval. */
  expected_interval_minutes?: number | null;
  interval_unset?: boolean;
  /** How the job starts. Absent on older APIs, which means 'schedule'. Event
   *  and manual jobs have no interval by nature and are never flagged for one. */
  trigger_type?: TriggerType;
  last_run_started_at?: string | null;
  last_run_finished_at?: string | null;
  last_run_outcome?: 'success' | 'failure' | null;
  last_run_duration_ms?: number | null;
  last_run_records?: number | null;
  last_run_error?: string | null;
};

/** One run from the new run-history table (ops_job_run), newest first. */
export type JobRun = {
  run_id: number;
  machine: string;
  started_at: string;
  finished_at: string | null;
  outcome: 'success' | 'failure' | null;
  duration_ms: number | null;
  records_handled: number | null;
  error_text: string | null;
};

export type CockpitOpsJobRunsResponse = {
  data: JobRun[];
};

export type CockpitOpsJobsResponse = {
  data: RegistryJob[];
};

export type UseCockpitOpsJobsResult = {
  jobs: RegistryJob[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
};

export function useCockpitOpsJobs(): UseCockpitOpsJobsResult {
  const query = useQuery<CockpitOpsJobsResponse, Error>({
    queryKey: ['cockpit-ops-jobs'],
    queryFn: () => apiGet<CockpitOpsJobsResponse>(`${COCKPIT_OPS_API_BASE}/jobs`),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 55_000,
  });
  return {
    jobs: query.data?.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
  };
}

export function useCockpitOpsHealth(): UseCockpitOpsHealthResult {
  const query = useQuery<CockpitOpsHealthResponse, Error>({
    queryKey: ['cockpit-ops-health'],
    queryFn: () =>
      apiGet<CockpitOpsHealthResponse>(`${COCKPIT_OPS_API_BASE}/health`),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 55_000,
  });
  const map = new Map<string, HealthRow>();
  for (const r of query.data?.rows ?? []) map.set(r.job_id, r);
  return {
    health: map,
    rows: query.data?.rows ?? [],
    fetchedAt: query.data?.fetched_at ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
  };
}

/**
 * Recent run history for one job from the new run-history table, fetched when
 * the job's detail modal opens. `jobName` null disables the query (old-only
 * jobs have no run history to fetch).
 */
export function useCockpitOpsJobRuns(jobName: string | null) {
  const query = useQuery<CockpitOpsJobRunsResponse, Error>({
    queryKey: ['cockpit-ops-job-runs', jobName],
    queryFn: () =>
      apiGet<CockpitOpsJobRunsResponse>(
        `${COCKPIT_OPS_API_BASE}/jobs/${encodeURIComponent(jobName ?? '')}/runs`,
      ),
    enabled: jobName !== null,
    staleTime: 55_000,
  });
  return {
    runs: query.data?.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** The facts the grid renders for one job, preferring the new job-monitoring
 *  system's data when the job reports through it (monitor === 'new') and
 *  falling back to the legacy registry fields otherwise. Pure, so it's unit
 *  tested. */
export type MonitorFacts = {
  /** True when the new system has this job (its facts below are the new ones). */
  isNew: boolean;
  /** Last run start (new) or last heartbeat (old). */
  lastRunAt: string | null;
  lastFinishedAt: string | null;
  outcome: 'success' | 'failure' | null;
  durationMs: number | null;
  records: number | null;
  errorText: string | null;
  /** New-monitored job whose expected interval nobody has set: a flagged gap. */
  intervalUnset: boolean;
  /** How the job starts; event and manual jobs have no interval by nature. */
  triggerType: TriggerType;
  /** Minutes between runs for the cadence column: the expected interval (new)
   *  or the registry cadence (old). Null when new + unset. */
  cadenceMinutes: number | null;
};

export function monitorFacts(job: RegistryJob): MonitorFacts {
  if (job.monitor === 'new') {
    return {
      isNew: true,
      lastRunAt: job.last_run_started_at ?? null,
      lastFinishedAt: job.last_run_finished_at ?? null,
      outcome: job.last_run_outcome ?? null,
      durationMs: job.last_run_duration_ms ?? null,
      records: job.last_run_records ?? null,
      errorText: job.last_run_error ?? null,
      intervalUnset: triggerTypeOf(job) === 'schedule' && (job.interval_unset ?? false),
      triggerType: triggerTypeOf(job),
      cadenceMinutes: job.expected_interval_minutes ?? null,
    };
  }
  return {
    isNew: false,
    lastRunAt: job.last_seen_at,
    lastFinishedAt: null,
    outcome: null,
    durationMs: job.duration_ms,
    records: null,
    errorText: null,
    intervalUnset: false,
    triggerType: triggerTypeOf(job),
    cadenceMinutes: job.cadence_minutes,
  };
}

/**
 * Human-friendly relative time, past ("5 minutes ago") and future
 * ("in 2 hours") — this page renders next-run times as well as last-seen.
 * Delegates to the shared `timeAgo`; kept as an export because the IT pages
 * import it from here.
 */
export function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  return timeAgo(iso) || iso;
}

export function formatLocalTime(iso: string | null): string {
  if (!iso) return '';
  return _formatDateTime(iso);
}

/** Render a run duration (milliseconds) compactly: "820ms", "4.2s", "1m 5s".
 *  Returns the em-dash-free placeholder "-" when the value is null/invalid so
 *  the grid shows a stable dash until the heartbeat poster includes
 *  duration_ms. */
export function formatDurationMs(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) return `${totalSeconds.toFixed(1)}s`;
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.round(totalSeconds - mins * 60);
  return `${mins}m ${secs}s`;
}
